import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import dayjs from "dayjs";
import api from "../../../api/api";
import Swal from "sweetalert2";

import sucessAlertAudio from "./audio/sucessAlert.mp3";
import checkAlertAudio from "./audio/checkAlert.mp3";
import declarationAlertAudio from "./audio/declarationAlert.mp3";
import enrollmentStatusAndDateAudio from "./audio/enrollmentststusanddate.mp3";
import entranceExaminationRequiredAudio from "./audio/entranceexaminationrequired.mp3";

import {
    UploadOutlined,
    UserAddOutlined,
    BookOutlined,
    IdcardOutlined,
    HeartOutlined,
    TeamOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    SolutionOutlined,
    SafetyOutlined,
    DeleteOutlined,
    EnvironmentOutlined,
    PhoneOutlined,
    MailOutlined,
    BankOutlined,
    CloudUploadOutlined,
} from "@ant-design/icons";

import {
    Form,
    Input,
    Select,
    Button,
    Card,
    Row,
    Col,
    Typography,
    DatePicker,
    Upload,
    Checkbox,
    Tag,
    Progress,
    Tooltip,
    Alert,
    Spin,
    Divider,
    Modal,
} from "antd";

const { Title, Text } = Typography;

const ADMISSION_ENDPOINT = "/api/users/students/";
const ENTRANCE_NOT_PASSED_MESSAGE =
    "You are not an eligible candidate for this Admission Process, because you didn't sit for the Entrance.";
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

const DOCUMENT_TYPES = [
    {
        value: "passport_photo",
        label: "Passport-size photograph",
    },
    {
        value: "national_id_passport",
        label: "National identification / passport",
    },
    {
        value: "academic_certificates",
        label: "Academic certificates / diploma",
    },
    {
        value: "academic_transcript",
        label: "Academic transcript / report",
    },
    {
        value: "other_supporting_documents",
        label: "Other supporting documents",
    },
];

const STAFF_ROLES = new Set([
    "admin",
    "principal",
    "proprietor",
    "registry",
    "admission officer",
    "business manager",
    "teacher",
    "vpi",
    "vpa",
    "dean",
    "it personel",
]);

const normalizeRole = (role) =>
    String(role || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

const readStoredUser = () => {
    const keys = [
        "user",
        "currentUser",
        "authUser",
        "user_data",
        "userData",
    ];

    for (const key of keys) {
        try {
            const raw = localStorage.getItem(key);

            if (!raw) continue;

            const parsed = JSON.parse(raw);

            const candidate =
                parsed?.user ||
                parsed?.data?.user ||
                parsed?.data ||
                parsed;

            if (candidate && typeof candidate === "object") {
                const role =
                    candidate.user_type ??
                    candidate.userType ??
                    candidate.role ??
                    candidate.type;

                if (role) {
                    return {
                        ...candidate,
                        resolvedRole: role,
                    };
                }
            }
        } catch {
            // Continue checking the remaining storage keys.
        }
    }

    return {
        resolvedRole:
            localStorage.getItem("user_type") ||
            localStorage.getItem("userType") ||
            localStorage.getItem("role") ||
            "",
    };
};

const EMPTY_STUDENT = {
    user: undefined,
    full_name: "",
    gender: undefined,
    county_of_origin: "",
    place_of_origin: "",
    district: "",
    location: "",
    grade_class: undefined,
    division_assigned: undefined,
    major_subject: "",
    previous_school: "",
    higest_qualification_earn: "",
    year_completed: "",
    certificate_diploma_grade: "",
    date_of_birth: null,
    address: "",
    student_admission_image: [],
    parent_guardian_name: "",
    relationship: "",
    parent_guardian_phone: "",
    parent_guardian_address: "",
    parent_guardian_email: "",
    employer_institution: "",
    position: "",
    years_of_service: "",
    work_phone: "",
    marital_status: undefined,
    health_status: "",
    phone_number: "",
    email_address: "",
    enrollment_date: null,
    student_status: undefined,
    status: undefined,
    nationality: "",
    academic_year: undefined,
};

const normalizeResponse = (data) => {
    if (Array.isArray(data)) return data;

    if (Array.isArray(data?.results)) return data.results;

    if (Array.isArray(data?.data)) return data.data;

    if (Array.isArray(data?.academic_years)) {
        return data.academic_years;
    }

    return [];
};

const getAdmissionId = (data) =>
    data?.id ??
    data?.admission?.id ??
    data?.student_admission?.id ??
    data?.data?.id ??
    null;

const getOptionLabel = (item) =>
    item?.academic_year ??
    item?.year ??
    item?.name ??
    item?.title ??
    item?.grade_name ??
    item?.division_name ??
    item?.username ??
    item?.email ??
    `Record ${item?.id ?? ""}`;

const formatFileSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (!data) {
        return (
            error?.message ||
            "Unable to complete the request. Check your connection and try again."
        );
    }

    if (typeof data === "string") return data;

    if (data.detail) return String(data.detail);

    if (data.message) return String(data.message);

    if (data.error) return String(data.error);

    return (
        Object.entries(data)
            .map(([field, value]) => {
                const detail = Array.isArray(value)
                    ? value
                          .map((item) =>
                              typeof item === "string"
                                  ? item
                                  : JSON.stringify(item)
                          )
                          .join(", ")
                    : typeof value === "string"
                      ? value
                      : JSON.stringify(value);

                return detail ? `${field}: ${detail}` : "";
            })
            .filter(Boolean)
            .join("\n") ||
        "Unable to complete the request. Check your entries."
    );
};

const brandedSwal = (options = {}) =>
    Swal.fire({
        buttonsStyling: false,
        reverseButtons: true,
        heightAuto: false,
        customClass: {
            popup: "admission-swal-popup",
            title: "admission-swal-title",
            htmlContainer: "admission-swal-content",
            confirmButton: "admission-swal-confirm",
            cancelButton: "admission-swal-cancel",
            actions: "admission-swal-actions",
        },
        confirmButtonText: "Continue",
        ...options,
    });

const playAdmissionAudio = (audioSource) => {
    try {
        const audio = new Audio(audioSource);
        audio.volume = 1;

        const playPromise = audio.play();

        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                console.warn(
                    "Unable to play admission alert audio:",
                    error
                );
            });
        }

        return audio;
    } catch (error) {
        console.warn(
            "Unable to initialize admission alert audio:",
            error
        );

        return null;
    }
};

const SectionHeader = ({
    icon,
    title,
    description,
    color,
    background,
}) => (
    <div className="admission-section-header">
        <div
            className="admission-section-icon"
            style={{ color, background }}
        >
            {icon}
        </div>

        <div className="admission-section-heading">
            <Title level={4} className="admission-section-title">
                {title}
            </Title>

            <Text className="admission-section-description">
                {description}
            </Text>
        </div>
    </div>
);

const SectionDivider = () => (
    <Divider className="admission-section-divider" />
);

const AdmissionForm = () => {
    const [form] = Form.useForm();

    const [accessToken] = useState(
        () => localStorage.getItem("access")
    );

    const [storedUser] = useState(() => readStoredUser());

    const userRole = normalizeRole(storedUser?.resolvedRole);

    const isAuthorizedStaff =
        Boolean(accessToken) && STAFF_ROLES.has(userRole);

    const isPublicApplicant = !accessToken;

    const hasUnauthorizedSession =
        Boolean(accessToken) && !isAuthorizedStaff;

    const [academicYears, setAcademicYears] = useState([]);
    const [grades, setGrades] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [users, setUsers] = useState([]);

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    const [documentFiles, setDocumentFiles] = useState({});
    const [documentsConfirmed, setDocumentsConfirmed] =
        useState(false);

    const submittingRef = useRef(false);

    const authHeaders = useMemo(
        () =>
            accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {},
        [accessToken]
    );

    const getAuthConfig = useCallback(
        () => ({ headers: authHeaders }),
        [authHeaders]
    );

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setLoadingData(true);

            const requests = [
                [
                    "grades",
                    "grade classes",
                    api.get(
                        "/api/users/grade-classes/",
                        getAuthConfig()
                    ),
                ],
                [
                    "divisions",
                    "departments",
                    api.get(
                        "/api/users/divisions/",
                        getAuthConfig()
                    ),
                ],
                [
                    "academicYears",
                    "academic years",
                    api.get(
                        "/academic-years/",
                        getAuthConfig()
                    ),
                ],
            ];

            try {
                const results = await Promise.allSettled(
                    requests.map((request) => request[2])
                );

                if (!mounted) return;

                const setters = {
                    grades: setGrades,
                    divisions: setDivisions,
                    academicYears: setAcademicYears,
                    users: setUsers,
                };

                const failures = [];

                results.forEach((result, index) => {
                    const [key, label] = requests[index];

                    if (result.status === "fulfilled") {
                        setters[key](
                            normalizeResponse(result.value.data)
                        );
                    } else {
                        failures.push(label);

                        console.error(
                            `Unable to load ${label}:`,
                            result.reason
                        );
                    }
                });

                const yearsResult = results[2];

                if (yearsResult.status === "fulfilled") {
                    const years = normalizeResponse(
                        yearsResult.value.data
                    );

                    const activeYear = years.find(
                        (year) =>
                            year.is_active === true ||
                            year.active === true ||
                            year.status === "active"
                    );

                    if (activeYear) {
                        form.setFieldValue(
                            "academic_year",
                            activeYear.id
                        );
                    }
                }

                if (failures.length) {
                    await brandedSwal({
                        icon: "warning",
                        title: "Some Information Could Not Load",
                        text: `Unable to load: ${failures.join(
                            ", "
                        )}. Check your connection and refresh the page.`,
                    });
                }
            } catch (error) {
                console.error(
                    "Unable to load admission form data:",
                    error
                );
            } finally {
                if (mounted) setLoadingData(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [getAuthConfig, form]);

    const handleStudentUserChange = (userId) => {
        if (!isAuthorizedStaff) return;

        if (!userId) {
            form.setFieldsValue({
                user: undefined,
                full_name: "",
                gender: undefined,
                email_address: "",
            });

            return;
        }

        const selectedUser = users.find(
            (item) => String(item.id) === String(userId)
        );

        if (!selectedUser) return;

        form.setFieldsValue({
            user: userId,
            full_name:
                selectedUser.full_name ||
                selectedUser.username ||
                selectedUser.name ||
                "",
            gender: selectedUser.gender || undefined,
            email_address:
                selectedUser.email ||
                selectedUser.email_address ||
                "",
        });
    };

    const handleDocumentFileChange = (
        documentType,
        fileList
    ) => {
        setDocumentFiles((previous) => ({
            ...previous,
            [documentType]: fileList
                .filter((item) => item.status !== "removed")
                .slice(-1),
        }));
    };

    const removeDocumentFile = (documentType) => {
        setDocumentFiles((previous) => ({
            ...previous,
            [documentType]: [],
        }));
    };

    const handleDeclarationChange = (event) => {
        const checked = event.target.checked;

        setDocumentsConfirmed(checked);

        if (checked) {
            playAdmissionAudio(declarationAlertAudio);
        }
    };

    const buildAdmissionFormData = (values) => {
        const formData = new FormData();

        const staffOnlyFields = [
            "user",
            "enrollment_date",
            "student_status",
            "status",
        ];

        Object.entries(values).forEach(([key, value]) => {
            if (
                key === "student_admission_image" ||
                key === "documents_submitted" ||
                value === undefined ||
                value === null ||
                value === ""
            ) {
                return;
            }

            if (
                !isAuthorizedStaff &&
                staffOnlyFields.includes(key)
            ) {
                return;
            }

            if (
                key === "date_of_birth" ||
                key === "enrollment_date"
            ) {
                if (typeof value?.format === "function") {
                    formData.append(
                        key,
                        value.format("YYYY-MM-DD")
                    );
                } else if (value) {
                    formData.append(key, String(value));
                }

                return;
            }

            if (
                typeof value === "object" &&
                !(
                    typeof File !== "undefined" &&
                    value instanceof File
                ) &&
                !(
                    typeof Blob !== "undefined" &&
                    value instanceof Blob
                )
            ) {
                return;
            }

            formData.append(key, value);
        });

        const imageItem =
            values.student_admission_image?.[0];

        const imageFile =
            imageItem?.originFileObj ||
            (typeof File !== "undefined" &&
            imageItem instanceof File
                ? imageItem
                : null);

        if (imageFile) {
            formData.append(
                "student_admission_image",
                imageFile
            );
        }

        DOCUMENT_TYPES.forEach(({ value }) => {
            const selectedItem = documentFiles[value]?.[0];

            const selectedFile =
                selectedItem?.originFileObj ||
                (typeof File !== "undefined" &&
                selectedItem instanceof File
                    ? selectedItem
                    : null);

            if (selectedFile) {
                formData.append(value, selectedFile);
            }
        });

        return formData;
    };


const showLoginCredentials = async (responseData) => {
    const credentials = responseData?.login_credentials || {};

    const username =
        responseData?.student_username ||
        credentials.username ||
        credentials.student_username ||
        responseData?.user?.username ||
        "";

    const password =
        responseData?.student_password ||
        credentials.initial_password ||
        credentials.password ||
        credentials.student_password ||
        "";

    const email =
        responseData?.student_email ||
        credentials.email ||
        responseData?.user?.email ||
        form.getFieldValue("email_address") ||
        "";

    const successMessage =
        responseData?.message ||
        "Your admission application and selected documents were submitted successfully.";

    Modal.success({
        title: username || password
            ? "Admission Created Successfully"
            : "Application Submitted Successfully",

        centered: true,
        width: 460,
        maskClosable: false,
        keyboard: false,
        closable: false,
        icon: <CheckCircleOutlined />,

        okText: username || password
            ? "I Have Saved My Credentials"
            : "Done",

        okButtonProps: {
            style: {
                background: "#062A4B",
                borderColor: "#062A4B",
                borderRadius: "8px",
                minHeight: "38px",
                fontWeight: 700,
                paddingInline: "18px",
            },
        },

        content: (
            <div className="admission-success-modal-content">
                <div className="admission-success-banner">
                    <div className="admission-success-icon">
                        <CheckCircleOutlined />
                    </div>

                    <div>
                        <strong>Application Successful!</strong>
                        <span>
                            Your admission information has been
                            submitted successfully.
                        </span>
                    </div>
                </div>

                {(username || password) && (
                    <>
                        <div className="admission-success-credentials-title">
                            Student Login Credentials
                        </div>

                        <div className="admission-success-credentials">
                            <div className="admission-success-credential-row">
                                <span>Student Username: </span>
                                <strong>
                                    {username || "Not provided"}
                                </strong>
                            </div>

                            <div className="admission-success-credential-row">
                                <span>Initial Password: </span>
                                <strong>
                                    {password || "Not provided"}
                                </strong>
                            </div>

                            {email && (
                                <div className="admission-success-credential-row">
                                    <span>Email Address: </span> <strong>{email}</strong>
                                </div>
                            )}
                        </div>

                        <div className="admission-success-security">
                            <SafetyOutlined />
                            <span>
                                Save these credentials securely.
                                Change your initial password after
                                signing in.
                            </span>
                        </div>
                    </>
                )}

                {!username && !password && (
                    <p className="admission-success-message">
                        {successMessage}
                    </p>
                )}
            </div>
        ),

        className: "admission-centered-success-modal",

        onOk: () => {
            playAdmissionAudio(sucessAlertAudio);
        },
    });
};





const validateEnrollmentFields = async () => {
    const enrollmentStatus = form.getFieldValue("status");
    const enrollmentDate = form.getFieldValue("enrollment_date");

    const missingFields = [];

    if (
        enrollmentStatus === undefined ||
        enrollmentStatus === null ||
        String(enrollmentStatus).trim() === ""
    ) {
        missingFields.push("Enrollment Status");
    }

    if (!enrollmentDate) {
        missingFields.push("Enrollment Date");
    }

    if (missingFields.length > 0) {
        playAdmissionAudio(enrollmentStatusAndDateAudio);

        await brandedSwal({
            icon: "warning",
            title: "Enrollment Information Required",
            html: `
                <div style="
                    text-align: left;
                    line-height: 1.45;
                    font-size: 13px;
                    padding: 0 4px;
                ">
                    <p style="margin: 0 0 6px;">
                        Please complete the following required field(s):
                    </p>

                    <ul style="
                        margin: 0 0 8px;
                        padding-left: 22px;
                    ">
                        ${missingFields
                            .map(
                                (field) => `
                                    <li style="margin: 2px 0;">
                                        <strong>${field}</strong>
                                    </li>
                                `
                            )
                            .join("")}
                    </ul>

                    <p style="margin: 0;">
                        Your application cannot be submitted until these
                        fields are completed.
                    </p>
                </div>
            `,
            confirmButtonText: "Complete Required Fields",
            buttonsStyling: false,
            allowOutsideClick: false,
            heightAuto: false,
            customClass: {
                popup: "enrollment-compact-popup",
                title: "enrollment-compact-title",
                htmlContainer: "enrollment-compact-content",
                confirmButton: "enrollment-compact-button",
            },
        });

        const firstMissingField = missingFields.includes(
            "Enrollment Status"
        )
            ? "status"
            : "enrollment_date";

        form.scrollToField(firstMissingField, {
            behavior: "smooth",
            block: "center",
        });

        return false;
    }

    return true;
};

   const handleSubmit = async (values) => {

            const enrollmentFieldsValid = await validateEnrollmentFields();
            if (!enrollmentFieldsValid) {
                return;
            }

        if (hasUnauthorizedSession) {
            await brandedSwal({
                icon: "error",
                title: "Staff Access Required",
                text: "Your current session does not identify an authorized admission staff role. Sign in with an authorized staff account or log out before using public self-admission.",
            });

            return;
        }

        if (submittingRef.current || loading) return;

        if (!documentsConfirmed) {
            playAdmissionAudio(checkAlertAudio);

            await brandedSwal({
                icon: "warning",
                title: "Declaration Required",
                text: "Please check the declaration confirming that the information provided is true and complete before submitting your application.",
                confirmButtonText: "I Understand",
            });

            return;
        }

        try {
            submittingRef.current = true;
            setLoading(true);

            const formData = buildAdmissionFormData(values);

            const response = await api.post(
                ADMISSION_ENDPOINT,
                formData,
                {
                    headers: { ...authHeaders },
                }
            );

            const responseData = response?.data || {};

            const admissionId = getAdmissionId(responseData);

            if (!admissionId) {
                console.error(
                    "Admission response:",
                    responseData
                );

                throw new Error(
                    "The server did not return an admission ID. Check the admission record in Django before trying again."
                );
            }

            showLoginCredentials(responseData);

            form.resetFields();
            setDocumentFiles({});
            setDocumentsConfirmed(false);

            const activeYear = academicYears.find(
                (year) =>
                    year.is_active === true ||
                    year.active === true ||
                    year.status === "active"
            );

            if (activeYear) {
                form.setFieldValue(
                    "academic_year",
                    activeYear.id
                );
            }

        } catch (error) {
            console.error(
                "Student admission submission failed:",
                error?.response?.data || error
            );

            const responseData = error?.response?.data || {};
            const responseStatus = error?.response?.status;

           const isEntranceEligibilityError =
    responseData?.code === "ENTRANCE_NOT_PASSED";

if (isEntranceEligibilityError) {
    const exactReason =
        typeof responseData?.detail === "string" &&
        responseData.detail.trim()
            ? responseData.detail.trim()
            : ENTRANCE_NOT_PASSED_MESSAGE;

    playAdmissionAudio(entranceExaminationRequiredAudio);

    await brandedSwal({
        icon: "error",
        title: "Entrance Examination Required",
        text: exactReason,
        confirmButtonText: "I Understand",
        allowOutsideClick: false,
        allowEscapeKey: false,
        heightAuto: false,
    });

    return;
}

            let errorMessage = getErrorMessage(error);

            if (
                responseStatus === 401 ||
                (
                    responseStatus === 403 &&
                    !responseData?.code
                )
            ) {
                errorMessage +=
                    "\n\nYour request was denied. If you are using public admission, verify that the public admission endpoint permits your request. Staff users should sign in with an authorized admission account.";
            }

            await brandedSwal({
                icon: "error",
                title: "Admission Could Not Be Completed",
                text: errorMessage,
                confirmButtonText: "Review and Try Again",
            });
        } finally {
            submittingRef.current = false;
            setLoading(false);
        }
    };

    const gradeOptions = grades.map((item) => ({
        value: item.id,
        label: getOptionLabel(item),
    }));

    const divisionOptions = divisions.map((item) => ({
        value: item.id,
        label: getOptionLabel(item),
    }));

    const academicYearOptions = academicYears.map((item) => ({
        value: item.id,
        label: getOptionLabel(item),
    }));

    const selectedFileCount = DOCUMENT_TYPES.filter(
        (document) => Boolean(documentFiles[document.value]?.[0])
    ).length;

    const field = (
        label,
        name,
        child,
        rules = [],
        extra = {}
    ) => (
        <Col xs={24} md={12} key={name}>
            <Form.Item
                label={label}
                name={name}
                rules={rules}
                {...extra}
            >
                {child}
            </Form.Item>
        </Col>
    );

    const input = (placeholder, props = {}) => (
        <Input
            placeholder={placeholder}
            className="admission-input"
            {...props}
        />
    );

    const textarea = (placeholder, rows = 3) => (
        <Input.TextArea
            rows={rows}
            placeholder={placeholder}
            className="admission-textarea"
        />
    );

    const select = (placeholder, options, props = {}) => (
        <Select
            showSearch
            optionFilterProp="label"
            placeholder={placeholder}
            className="admission-select"
            options={options}
            {...props}
        />
    );

    const section = (
        icon,
        title,
        description,
        background,
        color,
        children,
        className = ""
    ) => (
        <section
            className={`admission-section ${className}`}
        >
            <SectionHeader
                icon={icon}
                title={title}
                description={description}
                background={background}
                color={color}
            />

            <SectionDivider />

            {children}
        </section>
    );

    return (
        <div className="admission-page">
            <div className="admission-container">
                <header className="admission-hero">
                    <div className="admission-hero-decoration" />

                    <div className="admission-hero-content">
                        <div className="admission-hero-icon">
                            <UserAddOutlined />
                        </div>

                        <div className="admission-hero-copy">
                            <div className="admission-eyebrow">
                                {isPublicApplicant
                                    ? "STUDENT APPLICATION"
                                    : "SCHOOL ADMINISTRATION"}
                            </div>

                            <Title
                                level={2}
                                className="admission-page-title"
                            >
                                Student Admission
                            </Title>

                            <Text className="admission-hero-description">
                                {isPublicApplicant
                                    ? "Complete your application to create your student account."
                                    : "Register students, manage academic placement and submit admission documents."}
                            </Text>
                        </div>
                    </div>

                    <div className="admission-hero-badge">
                        <CheckCircleOutlined />

                        {isPublicApplicant
                            ? "Self-Admission"
                            : "Admission Management"}
                    </div>
                </header>

                {isPublicApplicant && (
                    <Alert
                        className="admission-info-alert"
                        type="info"
                        showIcon
                        message="Pay attention to the details of this admission process."
                        description="Enter the correct student details, select the academic year, and attach the required supporting documents before submitting."
                    />
                )}

                {hasUnauthorizedSession && (
                    <Alert
                        className="admission-info-alert"
                        type="error"
                        showIcon
                        message="Authorized staff access required"
                        description="Your current session does not identify an authorized admission staff role. Sign in with an authorized staff account or log out before using public self-admission."
                    />
                )}

                <Card
                    bordered={false}
                    className="admission-card"
                >
                    <Spin
                        spinning={loadingData}
                        tip="Loading admission information..."
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            initialValues={{ ...EMPTY_STUDENT }}
                            scrollToFirstError
                            disabled={
                                loading || hasUnauthorizedSession
                            }
                        >
                            {section(
                                <BookOutlined />,
                                "Academic Information",
                                "Choose the training year and the student's academic placement.",
                                "#E8F0FA",
                                "#063A6B",
                                <Row gutter={[18, 0]}>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Academic Year"
                                            name="academic_year"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select an academic year.",
                                                },
                                            ]}
                                        >
                                            {select(
                                                "Select academic year",
                                                academicYearOptions,
                                                {
                                                    loading: loadingData,
                                                    notFoundContent:
                                                        loadingData ? (
                                                            <Spin size="small" />
                                                        ) : (
                                                            "No academic years available"
                                                        ),
                                                }
                                            )}
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Grade Class / Program Level"
                                            name="grade_class"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Select grade class or program level.",
                                                },
                                            ]}
                                        >
                                            {select(
                                                "Select program level",
                                                gradeOptions,
                                                {
                                                    notFoundContent:
                                                        "No grade classes available",
                                                }
                                            )}
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Department"
                                            name="division_assigned"
                                        >
                                            {select(
                                                "Select department",
                                                divisionOptions,
                                                {
                                                    allowClear: true,
                                                    notFoundContent:
                                                        "No departments available",
                                                }
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>,
                                "academic-section"
                            )}

                            {section(
                                <IdcardOutlined />,
                                "Student Information",
                                "Enter the student's personal details and contact information.",
                                "#E5F5FC",
                                "#0877A8",
                                <>
                                    {isAuthorizedStaff &&
                                        users.length > 0 && (
                                            <Row gutter={[18, 0]}>
                                                <Col xs={24}>
                                                    <Form.Item
                                                        label="Link Existing User (Optional)"
                                                        name="user"
                                                    >
                                                        {select(
                                                            "Select an existing user",
                                                            users.map(
                                                                (item) => ({
                                                                    value: item.id,
                                                                    label:
                                                                        item.full_name ||
                                                                        item.username ||
                                                                        item.name ||
                                                                        item.email ||
                                                                        `Student ${item.id}`,
                                                                })
                                                            ),
                                                            {
                                                                allowClear: true,
                                                                onChange:
                                                                    handleStudentUserChange,
                                                            }
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        )}

                                    <Row gutter={[18, 0]}>
                                        {field(
                                            "Full Name",
                                            "full_name",
                                            input(
                                                "Enter student's full name",
                                                { maxLength: 150 }
                                            ),
                                            [
                                                {
                                                    required: true,
                                                    whitespace: true,
                                                    message:
                                                        "Enter the student's full name.",
                                                },
                                            ]
                                        )}

                                        {field(
                                            "Gender",
                                            "gender",
                                            select(
                                                "Select gender",
                                                [
                                                    {
                                                        value: "Male",
                                                        label: "Male",
                                                    },
                                                    {
                                                        value: "Female",
                                                        label: "Female",
                                                    },
                                                ],
                                                { showSearch: false }
                                            ),
                                            [
                                                {
                                                    required: true,
                                                    message:
                                                        "Select gender.",
                                                },
                                            ]
                                        )}

                                        {field(
                                            "Date of Birth",
                                            "date_of_birth",
                                            <DatePicker
                                                className="admission-input admission-date-picker"
                                                style={{ width: "100%" }}
                                                placeholder="Choose date of birth"
                                                format="YYYY-MM-DD"
                                                inputReadOnly={false}
                                                allowClear
                                                showToday={false}
                                                defaultPickerValue={dayjs().subtract(
                                                    18,
                                                    "years"
                                                )}
                                                disabledDate={(current) =>
                                                    current &&
                                                    current.isAfter(
                                                        dayjs().endOf("day")
                                                    )
                                                }
                                                popupClassName="admission-calendar-popup"
                                            />
                                        )}

                                        {field(
                                            "Nationality",
                                            "nationality",
                                            input("Enter nationality", {
                                                maxLength: 100,
                                            })
                                        )}

                                        {field(
                                            "Phone Number",
                                            "phone_number",
                                            input("Enter phone number", {
                                                maxLength: 50,
                                            })
                                        )}

                                        {field(
                                            "Email Address",
                                            "email_address",
                                            input(
                                                "Enter student email address",
                                                {
                                                    type: "email",
                                                    maxLength: 50,
                                                    autoComplete: "email",
                                                }
                                            ),
                                            [
                                                {
                                                    required:
                                                        isPublicApplicant,
                                                    message:
                                                        "Email address is required to create your student account.",
                                                },
                                                {
                                                    type: "email",
                                                    message:
                                                        "Enter a valid email address.",
                                                },
                                                {
                                                    max: 50,
                                                    message:
                                                        "Email address must not exceed 50 characters.",
                                                },
                                            ]
                                        )}

                                        {field(
                                            "County of Origin",
                                            "county_of_origin",
                                            input("Enter county of origin")
                                        )}

                                        {field(
                                            "Place of Origin",
                                            "place_of_origin",
                                            input("Enter place of origin")
                                        )}

                                        {field(
                                            "District",
                                            "district",
                                            input("Enter district")
                                        )}

                                        {field(
                                            "Current Location",
                                            "location",
                                            input("Enter current location")
                                        )}

                                        <Col xs={24}>
                                            <Form.Item
                                                label="Residential Address"
                                                name="address"
                                            >
                                                {textarea(
                                                    "Enter residential address"
                                                )}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>,
                                "student-section"
                            )}

                            {section(
                                <SolutionOutlined />,
                                "Educational Background",
                                "Provide the student's previous education and qualifications.",
                                "#EAF6E4",
                                "#428B27",
                                <Row gutter={[18, 0]}>
                                    {field(
                                        "Major Subject",
                                        "major_subject",
                                        input("Enter major subject")
                                    )}

                                    {field(
                                        "Previous School",
                                        "previous_school",
                                        input("Enter previous school")
                                    )}

                                    {field(
                                        "Highest Qualification Earned",
                                        "higest_qualification_earn",
                                        input(
                                            "e.g. High School Diploma"
                                        )
                                    )}

                                    {field(
                                        "Year Completed",
                                        "year_completed",
                                        input("Enter year completed")
                                    )}

                                    {field(
                                        "Certificate / Diploma Grade",
                                        "certificate_diploma_grade",
                                        input(
                                            "Enter certificate or diploma grade"
                                        )
                                    )}
                                </Row>,
                                "education-section"
                            )}

                            {section(
                                <CloudUploadOutlined />,
                                "Student Photograph",
                                "Upload a clear passport-size photograph.",
                                "#E5F5FC",
                                "#0877A8",
                                <Form.Item
                                    label="Student Admission Image"
                                    name="student_admission_image"
                                    valuePropName="fileList"
                                    getValueFromEvent={(event) =>
                                        event?.fileList || []
                                    }
                                >
                                    <Upload
                                        beforeUpload={(file) => {
                                            if (
                                                file.size >
                                                MAX_PHOTO_SIZE
                                            ) {
                                                brandedSwal({
                                                    icon: "warning",
                                                    title: "Photograph Too Large",
                                                    text: "The photograph must not exceed 5 MB.",
                                                });

                                                return Upload.LIST_IGNORE;
                                            }

                                            if (
                                                ![
                                                    "image/jpeg",
                                                    "image/png",
                                                    "image/webp",
                                                ].includes(file.type)
                                            ) {
                                                brandedSwal({
                                                    icon: "error",
                                                    title: "Unsupported Photograph",
                                                    text: "Choose a JPG, JPEG, PNG, or WEBP photograph.",
                                                });

                                                return Upload.LIST_IGNORE;
                                            }

                                            return false;
                                        }}
                                        maxCount={1}
                                        accept=".jpg,.jpeg,.png,.webp"
                                        listType="picture"
                                    >
                                        <Button
                                            icon={<UploadOutlined />}
                                            className="admission-upload-button"
                                        >
                                            Upload Student Photo
                                        </Button>
                                    </Upload>
                                </Form.Item>,
                                "photo-section"
                            )}

                            {section(
                                <TeamOutlined />,
                                "Parent / Guardian Information",
                                "Provide parent, guardian, or emergency contact details.",
                                "#EAF6E4",
                                "#428B27",
                                <Row gutter={[18, 0]}>
                                    {field(
                                        "Parent / Guardian Name",
                                        "parent_guardian_name",
                                        input("Enter parent or guardian name")
                                    )}

                                    {field(
                                        "Relationship",
                                        "relationship",
                                        input(
                                            "e.g. Mother, Father, Guardian"
                                        )
                                    )}

                                    {field(
                                        "Guardian Phone Number",
                                        "parent_guardian_phone",
                                        input("Enter guardian phone", {
                                            maxLength: 50,
                                        })
                                    )}

                                    {field(
                                        "Guardian Email Address",
                                        "parent_guardian_email",
                                        input("Enter guardian email", {
                                            type: "email",
                                            maxLength: 50,
                                        }),
                                        [
                                            {
                                                type: "email",
                                                message:
                                                    "Enter a valid guardian email address.",
                                            },
                                        ]
                                    )}

                                    <Col xs={24}>
                                        <Form.Item
                                            label="Parent / Guardian Address"
                                            name="parent_guardian_address"
                                        >
                                            {textarea(
                                                "Enter guardian address"
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>,
                                "guardian-section"
                            )}

                            {section(
                                <BankOutlined />,
                                "Employment Information",
                                "Complete this section if applicable.",
                                "#FFF2DB",
                                "#B36A09",
                                <Row gutter={[18, 0]}>
                                    {field(
                                        "Employer / Institution",
                                        "employer_institution",
                                        input(
                                            "Enter employer or institution"
                                        )
                                    )}

                                    {field(
                                        "Position",
                                        "position",
                                        input("Enter position")
                                    )}

                                    {field(
                                        "Years of Service",
                                        "years_of_service",
                                        input("Enter years of service")
                                    )}

                                    {field(
                                        "Work Phone",
                                        "work_phone",
                                        input("Enter work phone")
                                    )}
                                </Row>,
                                "employment-section"
                            )}

                            {section(
                                <HeartOutlined />,
                                "Health & Personal Information",
                                "Provide relevant health and marital status information.",
                                "#FCEBEC",
                                "#C44450",
                                <Row gutter={[18, 0]}>
                                    {field(
                                        "Marital Status",
                                        "marital_status",
                                        select(
                                            "Select marital status",
                                            [
                                                {
                                                    value: "single",
                                                    label: "Single",
                                                },
                                                {
                                                    value: "married",
                                                    label: "Married",
                                                },
                                                {
                                                    value: "other",
                                                    label: "Other",
                                                },
                                            ],
                                            {
                                                allowClear: true,
                                                showSearch: false,
                                            }
                                        )
                                    )}

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Health Status"
                                            name="health_status"
                                        >
                                            {textarea(
                                                "Enter relevant health information"
                                            )}
                                        </Form.Item>
                                    </Col>
                                </Row>,
                                "health-section"
                            )}

                        {section(
    <CalendarOutlined />,
    "Enrollment & Academic Status",
    "Set the student's enrollment date and admission status.",
    "#FFF2DB",
    "#B36A09",
    <Row gutter={[18, 0]}>
        {field(
            "Enrollment Date",
            "enrollment_date",
            <DatePicker
                style={{ width: "100%" }}
                className="admission-input admission-date-picker"
                placeholder="Select enrollment date"
                format="YYYY-MM-DD"
                allowClear
                popupClassName="admission-calendar-popup"
            />
        )}

        {field(
            "Enrollment Status",
            "status",
            select(
                "Select enrollment status",
                [
                    {
                        value: "Pending",
                        label: "Pending",
                    },
                    {
                        value: "Enrolled",
                        label: "Enrolled",
                    },
                ],
                {
                    allowClear: true,
                    showSearch: false,
                }
            )
        )}
    </Row>,
    "enrollment-section"
)}

                            {section(
                                <FileTextOutlined />,
                                "Document Checklist & Uploads",
                                "Attach the supporting documents required for admission.",
                                "#FFF2DB",
                                "#B36A09",
                                <>
                                    <Alert
                                        className="document-checklist-note"
                                        type="info"
                                        showIcon
                                        message="Accepted formats: PDF, JPG, JPEG and PNG"
                                        description="Each document must not exceed 10 MB. Your admission and selected documents will be submitted together."
                                    />

                                    <div className="document-progress-card">
                                        <div className="document-progress-heading">
                                            <div>
                                                <strong>
                                                    Document selection
                                                </strong>

                                                <span>
                                                    {selectedFileCount} of{" "}
                                                    {DOCUMENT_TYPES.length}{" "}
                                                    document categories selected
                                                </span>
                                            </div>

                                            <Tag
                                                color={
                                                    selectedFileCount
                                                        ? "processing"
                                                        : "default"
                                                }
                                            >
                                                {selectedFileCount
                                                    ? "Files selected"
                                                    : "No files selected"}
                                            </Tag>
                                        </div>

                                        <Progress
                                            percent={Math.round(
                                                (selectedFileCount /
                                                    DOCUMENT_TYPES.length) *
                                                    100
                                            )}
                                            showInfo={false}
                                            strokeColor="#4FAE22"
                                            trailColor="#E9EDF1"
                                        />
                                    </div>

                                    <div className="admission-document-list">
                                        {DOCUMENT_TYPES.map(
                                            (document, index) => {
                                                const fileItem =
                                                    documentFiles[
                                                        document.value
                                                    ]?.[0];

                                                const file =
                                                    fileItem?.originFileObj ||
                                                    fileItem;

                                                return (
                                                    <div
                                                        className="admission-document-item"
                                                        key={document.value}
                                                    >
                                                        <div className="admission-document-number">
                                                            {index + 1}
                                                        </div>

                                                        <div className="admission-document-content">
                                                            <div className="admission-document-title-row">
                                                                <Text className="admission-document-title">
                                                                    {document.label}
                                                                </Text>

                                                                <Tag
                                                                    color={
                                                                        file
                                                                            ? "processing"
                                                                            : "default"
                                                                    }
                                                                >
                                                                    {file
                                                                        ? "Ready to submit"
                                                                        : "Not selected"}
                                                                </Tag>
                                                            </div>

                                                            {file && (
                                                                <div className="admission-selected-file">
                                                                    <FileTextOutlined />

                                                                    <div className="admission-selected-file-details">
                                                                        <span>
                                                                            {file.name}
                                                                        </span>

                                                                        <small>
                                                                            {formatFileSize(
                                                                                file.size
                                                                            )}
                                                                        </small>
                                                                    </div>

                                                                    <Tooltip title="Remove selected file">
                                                                        <Button
                                                                            type="text"
                                                                            danger
                                                                            icon={
                                                                                <DeleteOutlined />
                                                                            }
                                                                            disabled={
                                                                                loading
                                                                            }
                                                                            onClick={() =>
                                                                                removeDocumentFile(
                                                                                    document.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            )}

                                                            <div className="admission-document-actions">
                                                                <Upload
                                                                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                                                    maxCount={1}
                                                                    multiple={false}
                                                                    fileList={
                                                                        documentFiles[
                                                                            document.value
                                                                        ] || []
                                                                    }
                                                                    beforeUpload={(
                                                                        selectedFile
                                                                    ) => {
                                                                        const extensionAllowed =
                                                                            /\.(pdf|jpg|jpeg|png)$/i.test(
                                                                                selectedFile.name
                                                                            );

                                                                        const typeAllowed =
                                                                            [
                                                                                "application/pdf",
                                                                                "image/jpeg",
                                                                                "image/png",
                                                                            ].includes(
                                                                                selectedFile.type
                                                                            );

                                                                        if (
                                                                            !extensionAllowed ||
                                                                            !typeAllowed
                                                                        ) {
                                                                            brandedSwal({
                                                                                icon: "error",
                                                                                title: "Unsupported File",
                                                                                text: "Choose a PDF, JPG, JPEG or PNG file.",
                                                                            });

                                                                            return Upload.LIST_IGNORE;
                                                                        }

                                                                        if (
                                                                            selectedFile.size >
                                                                            MAX_DOCUMENT_SIZE
                                                                        ) {
                                                                            brandedSwal({
                                                                                icon: "warning",
                                                                                title: "File Too Large",
                                                                                text: "Each document must not exceed 10 MB.",
                                                                            });

                                                                            return Upload.LIST_IGNORE;
                                                                        }

                                                                        return false;
                                                                    }}
                                                                    onChange={({
                                                                        fileList,
                                                                    }) =>
                                                                        handleDocumentFileChange(
                                                                            document.value,
                                                                            fileList
                                                                        )
                                                                    }
                                                                    showUploadList={
                                                                        false
                                                                    }
                                                                    disabled={
                                                                        loading
                                                                    }
                                                                >
                                                                    <Button
                                                                        icon={
                                                                            <UploadOutlined />
                                                                        }
                                                                        className="admission-document-upload-button"
                                                                        disabled={
                                                                            loading
                                                                        }
                                                                    >
                                                                        {file
                                                                            ? "Choose Another File"
                                                                            : "Choose Document"}
                                                                    </Button>
                                                                </Upload>

                                                                {file && (
                                                                    <Text className="admission-document-ready-text">
                                                                        <CheckCircleOutlined />{" "}
                                                                        Selected
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>

       <div className="document-checklist-footer">
    <Title
        level={5}
        className="applicant-declaration-title"
    >
        APPLICANT'S DECLARATION
    </Title>

    <Checkbox
        checked={documentsConfirmed}
        onChange={handleDeclarationChange}
    >
        I declare that all information provided is true and complete.
        I understand that false information may result in the cancellation
        of my admission. If admitted, I agree to abide by the rules and
        policies of PASSION AIDS THE FUTURE.
    </Checkbox>

    <Text className="document-checklist-footer-hint">
        {selectedFileCount} document file(s) will be sent with this
        application.
    </Text>
</div>
                                </>,
                                "admission-document-section"
                            )}

                            <div className="admission-submit-area">
                                <div className="admission-submit-message">
                                    <div className="admission-submit-icon">
                                        <CheckCircleOutlined />
                                    </div>

                                    <div>
                                        <strong>
                                            {isPublicApplicant
                                                ? "Ready to submit your application?"
                                                : "Ready to submit?"}
                                        </strong>

                                        <span>
                                            Admission details and selected
                                            files will be submitted in one
                                            request.
                                        </span>
                                    </div>
                                </div>

                                <Button
                                    htmlType="submit"
                                    loading={loading}
                                    disabled={
                                        loading ||
                                        loadingData ||
                                        hasUnauthorizedSession
                                    }
                                    icon={
                                        !loading && <UserAddOutlined />
                                    }
                                    className="admission-submit-button"
                                >
                                    {loading
                                        ? "Submitting Admission..."
                                        : isPublicApplicant
                                          ? "Submit Application & Create Account"
                                          : "Submit Student Admission"}
                                </Button>
                            </div>
                        </Form>
                    </Spin>
                </Card>

                <footer className="admission-footer">
                    <span className="admission-footer-mark">
                        <CheckCircleOutlined />
                    </span>

                    <span>
                        Design by Polu Integrated School Management System
                    </span>
                </footer>
            </div>

{/* Note: Don't boder about the style, i took it out for brevity */}
            <style>{`
                .admission-page {
                    --admission-navy: #062A4B;
                    --admission-blue: #063A6B;
                    --admission-bright-blue: #0F6DAB;
                    --admission-gold: #F6C944;
                    --admission-orange: #C0750D;
                    --admission-green: #4FAE22;
                    --admission-text: #243447;
                    --admission-muted: #738194;
                    --admission-border: #E1E8EF;
                    --admission-surface: #FFFFFF;
                    --admission-background: #F3F6FA;

                    min-height: 100vh;
                    padding: 26px 18px 36px;
                    box-sizing: border-box;
                    background:
                        radial-gradient(
                            circle at 0% 0%,
                            rgba(15, 109, 171, 0.07),
                            transparent 30%
                        ),
                        linear-gradient(
                            145deg,
                            #F5F8FC 0%,
                            #EEF3F8 100%
                        );
                }

                .admission-container {
                    width: 100%;
                    max-width: 1240px;
                    margin: 0 auto;
                }

                .applicant-declaration-title {
    display: block;
    margin: 0 0 16px !important;
    padding: 12px 18px;
    background: #062A4B;
    color: #FFFFFF !important;
    font-size: 16px !important;
    font-weight: 800 !important;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    border-radius: 10px;
    border-left: 5px solid #F6C944;
    box-shadow: 0 3px 8px rgba(6, 42, 75, 0.15);
}

                .admission-hero {
                    position: relative;
                    isolation: isolate;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 24px;
                    min-height: 155px;
                    margin-bottom: 20px;
                    padding: 30px;
                    border: 1px solid rgba(255,255,255,.12);
                    border-radius: 22px;
                    background:
                        linear-gradient(
                            120deg,
                            #062A4B 0%,
                            #063A6B 62%,
                            #0F6DAB 100%
                        );
                    box-shadow: 0 15px 38px rgba(6,42,75,.16);
                }

                .admission-hero-decoration {
                    position: absolute;
                    z-index: -1;
                    top: -115px;
                    right: 7%;
                    width: 290px;
                    height: 290px;
                    border: 42px solid rgba(246,201,68,.08);
                    border-radius: 50%;
                    pointer-events: none;
                }

                .admission-hero-decoration::after {
                    position: absolute;
                    content: "";
                    inset: 28px;
                    border: 1px solid rgba(255,255,255,.12);
                    border-radius: 50%;
                }

                .admission-hero-content {
                    display: flex;
                    align-items: center;
                    gap: 19px;
                    min-width: 0;
                }

                .admission-hero-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 64px;
                    height: 64px;
                    border: 1px solid rgba(246,201,68,.4);
                    border-radius: 18px;
                    background: rgba(246,201,68,.13);
                    color: var(--admission-gold);
                    font-size: 28px;
                    transition:
                        transform .25s ease,
                        background .25s ease;
                }

                .admission-hero:hover .admission-hero-icon {
                    transform: translateY(-3px) rotate(-3deg);
                    background: rgba(246,201,68,.2);
                }

                .admission-hero-copy {
                    min-width: 0;
                }

                .admission-eyebrow {
                    margin-bottom: 7px;
                    color: var(--admission-gold);
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: 1.7px;
                }

                .admission-page-title.ant-typography {
                    margin: 0;
                    color: #FFFFFF;
                    font-size: 30px;
                    line-height: 1.2;
                    font-weight: 800;
                    letter-spacing: -.7px;
                }

                .admission-hero-description {
                    display: block;
                    max-width: 600px;
                    margin-top: 8px;
                    color: rgba(255,255,255,.78);
                    font-size: 13px;
                    line-height: 1.7;
                }

                .admission-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 9px;
                    flex-shrink: 0;
                    padding: 12px 16px;
                    border: 1px solid rgba(246,201,68,.25);
                    border-radius: 12px;
                    background: rgba(255,255,255,.07);
                    color: #F6C944;
                    font-size: 12px;
                    font-weight: 700;
                }

                .admission-info-alert {
                    margin-bottom: 18px;
                    border-radius: 13px;
                    box-shadow: 0 4px 14px rgba(6,42,75,.035);
                }

                .admission-card.ant-card {
                    overflow: visible;
                    border: 1px solid rgba(221,230,239,.9);
                    border-radius: 19px;
                    background: var(--admission-surface);
                    box-shadow: 0 12px 38px rgba(6,42,75,.065);
                }

                .admission-card .ant-card-body {
                    padding: 30px;
                }

                .admission-section {
                    margin-bottom: 31px;
                    padding: 22px;
                    border: 1px solid #E8EDF3;
                    border-radius: 15px;
                    background: #FFFFFF;
                    transition:
                        border-color .22s ease,
                        box-shadow .22s ease,
                        transform .22s ease;
                }

                .admission-section:hover {
                    border-color: #D1DFEC;
                    box-shadow: 0 7px 22px rgba(6,42,75,.045);
                }

                .admission-section-header {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    margin-bottom: 16px;
                }

                .admission-section-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 45px;
                    height: 45px;
                    border: 1px solid rgba(6,42,75,.035);
                    border-radius: 13px;
                    font-size: 21px;
                    transition:
                        transform .2s ease,
                        border-radius .2s ease;
                }

                .admission-section:hover .admission-section-icon {
                    transform: translateY(-2px);
                    border-radius: 15px;
                }

                .admission-section-heading {
                    min-width: 0;
                }

                .admission-section-title.ant-typography {
                    margin: 0 0 4px;
                    color: var(--admission-navy);
                    font-size: 17px;
                    font-weight: 800;
                    line-height: 1.4;
                }

                .admission-section-description {
                    display: block;
                    color: var(--admission-muted);
                    font-size: 11.5px;
                    line-height: 1.65;
                }

                .admission-section-divider.ant-divider {
                    margin: 0 0 21px;
                    border-color: #E9EEF3;
                }

                .admission-card .ant-form-item {
                    margin-bottom: 19px;
                }

                .admission-card .ant-form-item-label {
                    padding-bottom: 7px;
                }

                .admission-card .ant-form-item-label > label {
                    color: #34465B;
                    font-size: 12px;
                    font-weight: 700;
                }

                .admission-card .ant-form-item-required::before {
                    color: #D94C4C !important;
                }

                .admission-input.ant-input,
                .admission-input.ant-picker {
                    width: 100%;
                    min-height: 45px;
                    border: 1px solid #D9E2EB;
                    border-radius: 9px;
                    background: #FFFFFF;
                    color: #243447;
                    font-size: 13px;
                    box-shadow: none;
                    transition:
                        border-color .2s ease,
                        box-shadow .2s ease,
                        background .2s ease;
                }

                .admission-input.ant-picker {
                    display: flex;
                    align-items: center;
                    padding: 0 12px;
                }

                .admission-input.ant-input {
                    padding: 10px 12px;
                }

                .admission-input.ant-input:hover,
                .admission-input.ant-picker:hover {
                    border-color: #0F6DAB;
                    background: #FCFEFF;
                }

                .admission-input.ant-input:focus,
                .admission-input.ant-input-focused,
                .admission-input.ant-picker-focused {
                    border-color: #0F6DAB;
                    box-shadow: 0 0 0 3px rgba(15,109,171,.10);
                }

                .admission-input input,
                .admission-input .ant-picker-input > input {
                    color: #243447;
                    font-size: 13px;
                }

                .admission-input input::placeholder,
                .admission-textarea::placeholder {
                    color: #9AA6B5;
                }

                .admission-select {
                    width: 100%;
                }

                .admission-select .ant-select-selector {
                    min-height: 45px !important;
                    padding: 5px 11px !important;
                    border: 1px solid #D9E2EB !important;
                    border-radius: 9px !important;
                    box-shadow: none !important;
                    transition:
                        border-color .2s ease,
                        box-shadow .2s ease !important;
                }

                .admission-select .ant-select-selection-item,
                .admission-select .ant-select-selection-placeholder {
                    line-height: 33px !important;
                    font-size: 13px;
                }

                .admission-select:hover .ant-select-selector,
                .admission-select.ant-select-focused .ant-select-selector {
                    border-color: #0F6DAB !important;
                    box-shadow: 0 0 0 3px rgba(15,109,171,.08) !important;
                }

                .admission-textarea.ant-input {
                    padding: 11px 12px;
                    border-color: #D9E2EB;
                    border-radius: 9px;
                    font-size: 13px;
                    line-height: 1.65;
                    resize: vertical;
                    transition:
                        border-color .2s ease,
                        box-shadow .2s ease;
                }

                .admission-textarea.ant-input:hover,
                .admission-textarea.ant-input:focus {
                    border-color: #0F6DAB;
                    box-shadow: 0 0 0 3px rgba(15,109,171,.08);
                }

                .admission-date-picker .ant-picker-input > input {
                    height: 28px;
                    cursor: text;
                }

                .admission-date-picker .ant-picker-suffix {
                    color: #0F6DAB;
                    font-size: 16px;
                }

                .admission-calendar-popup {
                    z-index: 1200;
                }

                .admission-calendar-popup .ant-picker-panel {
                    border-radius: 12px;
                }

                .admission-calendar-popup .ant-picker-header {
                    min-height: 44px;
                }

                .admission-calendar-popup .ant-picker-header button {
                    color: #063A6B;
                    transition:
                        background .2s ease,
                        color .2s ease;
                }

                .admission-calendar-popup .ant-picker-header button:hover {
                    border-radius: 6px;
                    background: #E8F0FA;
                    color: #0F6DAB;
                }

                .admission-calendar-popup .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
                    background: #063A6B;
                    border-radius: 7px;
                }

                .admission-calendar-popup .ant-picker-cell-in-view:not(.ant-picker-cell-disabled):hover .ant-picker-cell-inner {
                    border-radius: 7px;
                    background: #E8F0FA;
                    color: #063A6B;
                }

                .admission-upload-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    height: 43px;
                    padding: 0 16px;
                    border-color: #D2E0EC;
                    border-radius: 9px;
                    color: #063A6B;
                    font-weight: 700;
                    transition:
                        transform .2s ease,
                        background .2s ease,
                        border-color .2s ease;
                }

                .admission-upload-button:hover {
                    transform: translateY(-2px);
                    border-color: #0F6DAB !important;
                    background: #F1F7FC !important;
                    color: #0F6DAB !important;
                }

                .document-checklist-note {
                    margin-bottom: 19px;
                    border-radius: 10px;
                }

                .document-progress-card {
                    margin-bottom: 19px;
                    padding: 18px;
                    border: 1px solid #E4EAF0;
                    border-radius: 12px;
                    background: linear-gradient(
                        120deg,
                        #F8FAFC,
                        #FFFFFF
                    );
                }

                .document-progress-heading {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 13px;
                }

                .document-progress-heading strong,
                .document-progress-heading span {
                    display: block;
                }

                .document-progress-heading strong {
                    margin-bottom: 5px;
                    color: #062A4B;
                    font-size: 13px;
                }

                .document-progress-heading span {
                    color: #738194;
                    font-size: 11px;
                }

                .admission-document-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .admission-document-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 17px;
                    border: 1px solid #E0E7EF;
                    border-radius: 12px;
                    background: #FFFFFF;
                    transition:
                        transform .2s ease,
                        border-color .2s ease,
                        box-shadow .2s ease;
                }

                .admission-document-item:hover {
                    transform: translateY(-2px);
                    border-color: #B7CEE0;
                    box-shadow: 0 7px 20px rgba(6,42,75,.06);
                }

                .admission-document-number {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 35px;
                    height: 35px;
                    border: 1px solid #DDE8F1;
                    border-radius: 10px;
                    background: #EDF4FA;
                    color: #063A6B;
                    font-size: 13px;
                    font-weight: 800;
                }

                .admission-document-content {
                    flex: 1;
                    min-width: 0;
                }

                .admission-document-title-row {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 8px;
                }

                .admission-document-title {
                    color: #25364A;
                    font-size: 12.5px;
                    font-weight: 700;
                    line-height: 1.7;
                }

                .admission-selected-file {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-top: 13px;
                    padding: 11px;
                    border: 1px solid #D9EACF;
                    border-radius: 9px;
                    background: #F6FBF2;
                    color: #438C29;
                }

                .admission-selected-file > .anticon {
                    flex-shrink: 0;
                    font-size: 18px;
                }

                .admission-selected-file-details {
                    flex: 1;
                    min-width: 0;
                }

                .admission-selected-file-details span {
                    display: block;
                    overflow-wrap: anywhere;
                    color: #344054;
                    font-size: 12px;
                }

                .admission-selected-file-details small {
                    display: block;
                    margin-top: 3px;
                    color: #7B8794;
                    font-size: 10px;
                }

                .admission-document-actions {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 11px;
                    margin-top: 13px;
                }

                .admission-document-upload-button {
                    height: 36px;
                    padding: 0 13px;
                    border-color: #CBD9E5;
                    border-radius: 8px;
                    color: #063A6B;
                    font-size: 11px;
                    font-weight: 700;
                    transition: all .2s ease;
                }

                .admission-document-upload-button:hover {
                    transform: translateY(-1px);
                    border-color: #0F6DAB !important;
                    background: #F0F7FC !important;
                    color: #0F6DAB !important;
                }

                .admission-document-ready-text {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    color: #438C29;
                    font-size: 11px;
                    font-weight: 700;
                }

                .document-checklist-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-top: 20px;
                    padding: 17px;
                    border: 1px solid #E2E9F0;
                    border-radius: 11px;
                    background: #F8FAFC;
                }

                .document-checklist-footer .ant-checkbox-wrapper {
                    color: #34465B;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 1.8;
                }

                .document-checklist-footer-hint {
                    color: #738194;
                    font-size: 11px;
                }

                .admission-submit-area {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 20px;
                    margin-top: 7px;
                    padding: 25px 5px 0;
                    border-top: 1px solid #E8EDF3;
                }

                .admission-submit-message {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                }

                .admission-submit-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 44px;
                    height: 44px;
                    border-radius: 13px;
                    background: #EAF6E4;
                    color: #438C29;
                    font-size: 20px;
                }

                .admission-submit-message strong {
                    display: block;
                    color: #062A4B;
                    font-size: 13px;
                    font-weight: 800;
                }

                .admission-submit-message span {
                    display: block;
                    max-width: 450px;
                    margin-top: 5px;
                    color: #738194;
                    font-size: 11px;
                    line-height: 1.7;
                }

                .admission-submit-button.ant-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    min-width: 255px;
                    min-height: 49px;
                    height: auto;
                    padding: 13px 22px;
                    border: none;
                    border-radius: 11px;
                    background: linear-gradient(
                        110deg,
                        #062A4B,
                        #0A5285
                    );
                    color: #FFFFFF;
                    font-size: 12px;
                    font-weight: 800;
                    white-space: normal;
                    box-shadow: 0 7px 18px rgba(6,42,75,.18);
                    transition:
                        transform .2s ease,
                        box-shadow .2s ease,
                        background .2s ease;
                }

                .admission-submit-button.ant-btn:hover,
                .admission-submit-button.ant-btn:focus {
                    transform: translateY(-2px);
                    background: linear-gradient(
                        110deg,
                        #0A3A61,
                        #0F6DAB
                    ) !important;
                    color: #FFFFFF !important;
                    box-shadow: 0 10px 23px rgba(6,42,75,.23);
                }

                .admission-submit-button.ant-btn:active {
                    transform: translateY(0);
                }

                .admission-submit-button.ant-btn[disabled] {
                    transform: none;
                    box-shadow: none;
                }

                .admission-footer {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 24px 10px 0;
                    color: #8A96A5;
                    font-size: 11px;
                    text-align: center;
                }

                .admission-footer-mark {
                    color: #4FAE22;
                }

                .admission-swal-popup {
                    border-radius: 18px !important;
                }

                .admission-swal-title {
                    color: #062A4B !important;
                    font-weight: 800 !important;
                }

                .admission-swal-content {
                    color: #475467 !important;
                    line-height: 1.7 !important;
                    white-space: pre-line;
                }

                .admission-swal-actions {
                    gap: 10px !important;
                }

                .admission-swal-confirm,
                .admission-swal-cancel {
                    min-height: 41px;
                    padding: 10px 18px;
                    border: 0;
                    border-radius: 9px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background .2s ease;
                }

                .admission-swal-confirm {
                    background: #062A4B;
                    color: #FFFFFF;
                }

                .admission-swal-confirm:hover {
                    background: #0F6DAB;
                }

                .admission-swal-cancel {
                    background: #EEF1F5;
                    color: #344054;
                }

                .admission-swal-success {
                    margin-bottom: 15px;
                    padding: 14px;
                    border: 1px solid #D8EBBD;
                    border-radius: 11px;
                    background: #F5FBE9;
                    color: #365314;
                }

                .admission-swal-credentials {
                    overflow: hidden;
                    border: 1px solid #E6E8EE;
                    border-radius: 11px;
                    text-align: left;
                }

                .admission-swal-credentials > div {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    padding: 13px 15px;
                    border-bottom: 1px solid #EDF0F4;
                }

                .admission-swal-credentials > div:last-child {
                    border-bottom: 0;
                }

                .admission-swal-credentials span {
                    color: #667085;
                    font-size: 11px;
                }

                .admission-swal-credentials strong {
                    overflow-wrap: anywhere;
                    color: #062A4B;
                    font-size: 14px;
                }

                .admission-swal-security {
                    margin-top: 13px;
                    padding: 13px;
                    border-radius: 9px;
                    background: #FFF7E6;
                    color: #7C4A03;
                    font-size: 12px;
                    text-align: left;
                }

                @media (min-width: 769px) {
                    .admission-section {
                        padding: 25px;
                    }
                }

                @media (max-width: 900px) {
                    .admission-page {
                        padding: 18px 12px 28px;
                    }

                    .admission-hero {
                        padding: 24px;
                    }

                    .admission-hero-badge {
                        padding: 10px 12px;
                        font-size: 10px;
                    }

                    .admission-card .ant-card-body {
                        padding: 22px;
                    }
                }

                @media (max-width: 768px) {
                    .admission-page {
                        padding: 12px 9px 25px;
                    }

                    .admission-hero {
                        align-items: flex-start;
                        flex-direction: column;
                        gap: 17px;
                        padding: 21px;
                        border-radius: 16px;
                    }

                    .admission-hero-content {
                        align-items: flex-start;
                        gap: 13px;
                    }

                    .admission-hero-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 13px;
                        font-size: 22px;
                    }

                    .admission-page-title.ant-typography {
                        font-size: 23px;
                    }

                    

                    .admission-hero-description {
                        font-size: 12px;
                    }

                    .admission-hero-badge {
                        align-self: flex-start;
                    }

                    .admission-card .ant-card-body {
                        padding: 14px;
                    }

                    .admission-section {
                        margin-bottom: 19px;
                        padding: 16px;
                        border-radius: 12px;
                    }

                    .admission-section-icon {
                        width: 39px;
                        height: 39px;
                        border-radius: 11px;
                        font-size: 18px;
                    }

                    .admission-section-title.ant-typography {
                        font-size: 15px;
                    }

                    .admission-section-description {
                        font-size: 11px;
                    }

                    .admission-document-item {
                        padding: 13px;
                        gap: 10px;
                    }

                    .admission-document-number {
                        width: 30px;
                        height: 30px;
                    }

                    .admission-submit-area {
                        align-items: stretch;
                        flex-direction: column;
                        padding-top: 19px;
                    }

                    .admission-submit-button.ant-btn {
                        width: 100%;
                        min-width: 0;
                    }
                }

                @media (max-width: 480px) {
                    .admission-hero {
                        padding: 17px;
                    }

                    .admission-hero-icon {
                        width: 41px;
                        height: 41px;
                        min-width: 41px;
                        font-size: 19px;
                    }

                    .admission-eyebrow {
                        font-size: 9px;
                        letter-spacing: 1px;
                    }

                    .admission-page-title.ant-typography {
                        font-size: 20px;
                    }

                    .admission-hero-description {
                        font-size: 11px;
                    }

                    .admission-section {
                        padding: 13px;
                    }

                    .admission-section-header {
                        align-items: flex-start;
                        gap: 10px;
                    }

                    .admission-section-icon {
                        width: 35px;
                        height: 35px;
                        min-width: 35px;
                        font-size: 16px;
                    }

                    .admission-section-title.ant-typography {
                        font-size: 14px;
                    }

                    .document-progress-card {
                        padding: 12px;
                    }

                    .document-progress-heading {
                        align-items: flex-start;
                    }

                    .document-progress-heading span {
                        max-width: 190px;
                        line-height: 1.6;
                    }

                    .admission-document-title-row {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .document-checklist-footer {
                        align-items: flex-start;
                        flex-direction: column;
                        padding: 12px;
                    }

                    .admission-submit-message span {
                        font-size: 10.5px;
                    }

                    .admission-footer {
                        font-size: 10px;
                        line-height: 1.7;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .admission-page *,
                    .admission-page *::before,
                    .admission-page *::after {
                        animation-duration: .01ms !important;
                        animation-iteration-count: 1 !important;
                        scroll-behavior: auto !important;
                        transition-duration: .01ms !important;
                    }

                    
/* Centered application-success modal */
.admission-centered-success-modal {
    top: 50% !important;
    padding-bottom: 0 !important;
    transform: translateY(-50%) !important;
}

.admission-centered-success-modal .ant-modal-content {
    overflow: hidden;
    padding: 22px !important;
    border: 1px solid #E1E8EF;
    border-radius: 16px !important;
    background: #FFFFFF;
    box-shadow: 0 18px 55px rgba(6, 42, 75, 0.20);
}

.admission-centered-success-modal .ant-modal-header {
    margin-bottom: 14px !important;
    background: transparent !important;
}

.admission-centered-success-modal .ant-modal-title {
    color: #062A4B !important;
    font-size: 19px !important;
    font-weight: 800 !important;
}

.admission-centered-success-modal .ant-modal-body {
    padding: 0 !important;
}

.admission-centered-success-modal .ant-modal-footer {
    margin-top: 18px !important;
    padding-top: 14px !important;
    border-top: 1px solid #E9EEF3 !important;
}

.admission-centered-success-modal .ant-modal-footer .ant-btn {
    height: auto;
    min-height: 38px;
    white-space: normal;
}

.admission-success-modal-content {
    width: 100%;
    color: #344054;
}

.admission-success-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 14px;
    border: 1px solid #D8EBBD;
    border-radius: 11px;
    background: #F5FBE9;
}

.admission-success-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #E2F3D5;
    color: #438C29;
    font-size: 23px;
}

.admission-success-banner strong,
.admission-success-banner span {
    display: block;
}

.admission-success-banner strong {
    margin-bottom: 4px;
    color: #285B19;
    font-size: 14px;
    font-weight: 800;
}

.admission-success-banner span {
    color: #55704B;
    font-size: 11px;
    line-height: 1.6;
}

.admission-success-credentials-title {
    margin-bottom: 9px;
    color: #062A4B;
    font-size: 12px;
    font-weight: 800;
}

.admission-success-credentials {
    overflow: hidden;
    border: 1px solid #E1E8EF;
    border-radius: 10px;
}

.admission-success-credential-row {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px 12px;
    border-bottom: 1px solid #EDF0F4;
}

.admission-success-credential-row:last-child {
    border-bottom: none;
}

.admission-success-credential-row span {
    color: #738194;
    font-size: 10px;
    font-weight: 600;
}

.admission-success-credential-row strong {
    overflow-wrap: anywhere;
    color: #062A4B;
    font-size: 13px;
    font-weight: 800;
}

.admission-success-security {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-top: 12px;
    padding: 11px;
    border: 1px solid #F3DEAC;
    border-radius: 9px;
    background: #FFF7E6;
    color: #7C4A03;
    font-size: 11px;
    line-height: 1.65;
}

.admission-success-security .anticon {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 15px;
}

.admission-success-message {
    margin: 0;
    color: #475467;
    font-size: 12px;
    line-height: 1.7;
}

@media (max-width: 520px) {
    .admission-centered-success-modal {
        width: calc(100% - 28px) !important;
        max-width: 460px;
        margin: 0 auto;
    }

    .admission-centered-success-modal .ant-modal-content {
        padding: 16px !important;
        border-radius: 13px !important;
    }

    .admission-centered-success-modal .ant-modal-title {
        font-size: 16px !important;
    }

    .admission-success-banner {
        gap: 9px;
        padding: 11px;
    }

    .admission-success-icon {
        width: 36px;
        height: 36px;
        font-size: 19px;
    }

    .admission-success-banner strong {
        font-size: 12px;
    }

    .admission-success-banner span {
        font-size: 10px;
    }

    .enrollment-compact-popup {
    padding: 18px 22px 16px !important;
    border-radius: 14px !important;
    max-width: 420px !important;
}

.enrollment-compact-title {
    font-size: 19px !important;
    line-height: 1.25 !important;
    margin-bottom: 8px !important;
    padding: 0 !important;
}

.enrollment-compact-content {
    margin: 8px 0 12px !important;
    padding: 0 !important;
    font-size: 13px !important;
    line-height: 1.45 !important;
}


.enrollment-compact-button {
    display: inline-block !important;
    background: #062A4B !important;
    color: #FFFFFF !important;
    border: 1px solid #062A4B !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 8px 16px !important;
    min-height: 36px !important;
    border-radius: 7px !important;
    margin: 0 !important;
    cursor: pointer !important;
    opacity: 1 !important;
    box-shadow: none !important;
    transition: background 0.2s ease, transform 0.2s ease !important;
}

.enrollment-compact-button:hover {
    background: #0F6DAB !important;
    border-color: #0F6DAB !important;
    color: #FFFFFF !important;
}



}
                }
            `}</style>
        </div>
    );
};

export default AdmissionForm;